import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DomainRegistrationAdmin from './DomainRegistrationAdmin';
import CatalogProducts from './CatalogProducts';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import React, { Fragment } from 'react';
import { 
  Plus, ChevronDown, ChevronUp, Search, Edit2, Trash2, 
  Server, Cpu, HardDrive, Cloud, Globe, Shield, Mail, 
  Database, Zap, Lock, Package, FolderOpen, GripVertical,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLES = [
  { key: 'monthly', label: 'Monthly', months: 1 },
  { key: 'quarterly', label: 'Quarterly', months: 3 },
  { key: 'half_yearly', label: 'Half Yearly', months: 6 },
  { key: 'yearly', label: 'Yearly', months: 12 },
  { key: 'two_years', label: 'Two Years', months: 24 },
  { key: 'three_years', label: 'Three Years', months: 36 }
];

// Icon mapping for categories
const CATEGORY_ICONS = {
  'Server': Server,
  'Cpu': Cpu,
  'HardDrive': HardDrive,
  'Cloud': Cloud,
  'Globe': Globe,
  'Shield': Shield,
  'Mail': Mail,
  'Database': Database,
  'Zap': Zap,
  'Lock': Lock,
  'Package': Package
};

// Available colors for categories
const CATEGORY_COLORS = [
  { value: 'bg-blue-100 text-blue-700', label: 'Blue' },
  { value: 'bg-purple-100 text-purple-700', label: 'Purple' },
  { value: 'bg-red-100 text-red-700', label: 'Red' },
  { value: 'bg-sky-100 text-sky-700', label: 'Sky' },
  { value: 'bg-green-100 text-green-700', label: 'Green' },
  { value: 'bg-emerald-100 text-emerald-700', label: 'Emerald' },
  { value: 'bg-orange-100 text-orange-700', label: 'Orange' },
  { value: 'bg-amber-100 text-amber-700', label: 'Amber' },
  { value: 'bg-yellow-100 text-yellow-700', label: 'Yellow' },
  { value: 'bg-rose-100 text-rose-700', label: 'Rose' },
  { value: 'bg-slate-100 text-slate-700', label: 'Slate' },
  { value: 'bg-cyan-100 text-cyan-700', label: 'Cyan' },
  { value: 'bg-pink-100 text-pink-700', label: 'Pink' },
  { value: 'bg-indigo-100 text-indigo-700', label: 'Indigo' }
];

export default function AdminProducts() {
  const { canDelete, hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Permission checks
  const canCreateProduct = hasPermission('products:create');
  const canEditProduct = hasPermission('products:edit');
  const canDeleteProduct = hasPermission('products:delete') && canDelete;
  const canCreateCategory = hasPermission('categories:create');
  const canEditCategory = hasPermission('categories:edit');
  const canDeleteCategory = hasPermission('categories:delete') && canDelete;
  
  // Category management state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    icon: 'Package',
    color: 'bg-blue-100 text-blue-700',
    sort_order: 0
  });
  
  // Category Products Dialog state
  const [categoryProductsDialog, setCategoryProductsDialog] = useState(null);
  const [editingProductInCategory, setEditingProductInCategory] = useState(null);
  const [categoryProductFormData, setCategoryProductFormData] = useState({
    name: '',
    description: '',
    base_price: '',
    billing_prices: { monthly: '', quarterly: '', half_yearly: '', yearly: '', two_years: '', three_years: '' },
    enabled_billing_cycles: ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
    features: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    base_price: '',
    billing_prices: { monthly: '', quarterly: '', half_yearly: '', yearly: '', two_years: '', three_years: '' },
    enabled_billing_cycles: ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
    features: ''
  });

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Fallback to empty array if endpoint doesn't exist yet
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const handleBasePriceChange = (value) => {
    const basePrice = parseFloat(value) || 0;
    const newBillingPrices = {
      monthly: basePrice,
      quarterly: basePrice * 3,
      half_yearly: basePrice * 6,
      yearly: basePrice * 12,
      two_years: basePrice * 24,
      three_years: basePrice * 36
    };
    setFormData({
      ...formData,
      base_price: value,
      billing_prices: newBillingPrices
    });
  };

  const handleBillingPriceChange = (cycle, value) => {
    setFormData({
      ...formData,
      billing_prices: {
        ...formData.billing_prices,
        [cycle]: parseFloat(value) || 0
      }
    });
  };

  const handleToggleBillingCycle = (cycleKey) => {
    setFormData(prev => {
      const enabled = prev.enabled_billing_cycles.includes(cycleKey)
        ? prev.enabled_billing_cycles.filter(c => c !== cycleKey)
        : [...prev.enabled_billing_cycles, cycleKey];
      return { ...prev, enabled_billing_cycles: enabled };
    });
  };

  const handleToggleBillingCycleInCategory = (cycleKey) => {
    setCategoryProductFormData(prev => {
      const enabled = prev.enabled_billing_cycles.includes(cycleKey)
        ? prev.enabled_billing_cycles.filter(c => c !== cycleKey)
        : [...prev.enabled_billing_cycles, cycleKey];
      return { ...prev, enabled_billing_cycles: enabled };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.enabled_billing_cycles.length === 0) {
      toast.error('At least one billing cycle must be enabled');
      return;
    }
    const submitData = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      base_price: parseFloat(formData.base_price),
      billing_prices: formData.billing_prices,
      enabled_billing_cycles: formData.enabled_billing_cycles,
      features: formData.features.split(',').map(f => f.trim()).filter(f => f)
    };

    try {
      if (editProduct) {
        await axios.put(`${API}/admin/products/${editProduct.id}`, submitData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, submitData);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    const billingPrices = product.billing_prices || {
      monthly: product.base_price,
      quarterly: product.base_price * 3,
      half_yearly: product.base_price * 6,
      yearly: product.base_price * 12,
      two_years: product.base_price * 24,
      three_years: product.base_price * 36
    };
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      base_price: product.base_price.toString(),
      billing_prices: billingPrices,
      enabled_billing_cycles: product.enabled_billing_cycles && product.enabled_billing_cycles.length > 0
        ? product.enabled_billing_cycles
        : ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
      features: product.features.join(', ')
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete products');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      base_price: '',
      billing_prices: {
        monthly: '',
        quarterly: '',
        half_yearly: '',
        yearly: '',
        two_years: '',
        three_years: ''
      },
      enabled_billing_cycles: ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
      features: ''
    });
    setEditProduct(null);
  };

  // Category management handlers
  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      icon: 'Package',
      color: 'bg-blue-100 text-blue-700',
      sort_order: 0
    });
    setEditCategory(null);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editCategory) {
        await axios.put(`${API}/admin/categories/${editCategory.id}`, categoryFormData);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/categories`, categoryFormData);
        toast.success('Category created successfully');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditCategory = (category) => {
    setEditCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'Package',
      color: category.color || 'bg-blue-100 text-blue-700',
      sort_order: category.sort_order || 0
    });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryDialog) return;
    
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete categories');
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/categories/${deleteCategoryDialog.id}`);
      toast.success('Category deleted successfully');
      setDeleteCategoryDialog(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Category Products handlers
  const getCategoryProducts = (categoryName) => {
    return products.filter(p => p.category === categoryName);
  };

  const handleViewCategoryProducts = (category) => {
    setCategoryProductsDialog(category);
    setEditingProductInCategory(null);
  };

  const handleEditProductInCategory = (product) => {
    setEditingProductInCategory(product);
    setCategoryProductFormData({
      name: product.name,
      description: product.description || '',
      base_price: product.base_price || '',
      billing_prices: product.billing_prices || {
        monthly: product.base_price || '',
        quarterly: '',
        half_yearly: '',
        yearly: '',
        two_years: '',
        three_years: ''
      },
      enabled_billing_cycles: product.enabled_billing_cycles && product.enabled_billing_cycles.length > 0
        ? product.enabled_billing_cycles
        : ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
      features: product.features ? product.features.join(', ') : ''
    });
  };

  const handleCategoryProductFormChange = (field, value) => {
    setCategoryProductFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryProductBillingPriceChange = (cycle, value) => {
    setCategoryProductFormData(prev => ({
      ...prev,
      billing_prices: { ...prev.billing_prices, [cycle]: value }
    }));
  };

  const handleSaveProductInCategory = async (e) => {
    e.preventDefault();
    if (!editingProductInCategory) return;

    try {
      const submitData = {
        ...categoryProductFormData,
        features: categoryProductFormData.features.split(',').map(f => f.trim()).filter(f => f),
        enabled_billing_cycles: categoryProductFormData.enabled_billing_cycles,
        category: categoryProductsDialog.name // Keep same category
      };
      await axios.put(`${API}/admin/products/${editingProductInCategory.id}`, submitData);
      toast.success('Product updated successfully');
      setEditingProductInCategory(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const cancelEditProductInCategory = () => {
    setEditingProductInCategory(null);
    setCategoryProductFormData({
      name: '',
      description: '',
      base_price: '',
      billing_prices: {
        monthly: '',
        quarterly: '',
        half_yearly: '',
        yearly: '',
        two_years: '',
        three_years: ''
      },
      enabled_billing_cycles: ['monthly', 'quarterly', 'half_yearly', 'yearly', 'two_years', 'three_years'],
      features: ''
    });
  };

  const getCategoryIcon = (iconName) => {
    const Icon = CATEGORY_ICONS[iconName] || Package;
    return Icon;
  };

  const toggleExpand = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const getBillingPrices = (product) => {
    if (product.billing_prices) {
      return product.billing_prices;
    }
    return {
      monthly: product.base_price,
      quarterly: product.base_price * 3,
      half_yearly: product.base_price * 6,
      yearly: product.base_price * 12,
      two_years: product.base_price * 24,
      three_years: product.base_price * 36
    };
  };

  // Get unique categories for filter from both DB categories and products
  const productCategories = [...new Set(products.map(p => p.category))];
  const allCategoryNames = [...new Set([...categories.map(c => c.name), ...productCategories])];

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const tabs = [
    { id: 'products', label: 'Products' },
    { id: 'categories', label: 'Categories' },
    { id: 'domains', label: 'Domain Registration' },
    { id: 'catalog', label: 'Catalog Products' },
  ];

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" data-testid="products-page-title">Products</h1>
            <p className="text-slate-500 mt-1">Manage your reseller network and products</p>
          </div>
          {canCreateProduct && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle>{editProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Product Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="product-category-select">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.length > 0 ? (
                          categories.map((cat) => (
                            <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                          ))
                        ) : (
                          // Fallback to product categories if no DB categories
                          allCategoryNames.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    data-testid="product-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="base_price">Base Monthly Price (INR) *</Label>
                  <Input
                    id="base_price"
                    type="number"
                    step="0.01"
                    value={formData.base_price}
                    onChange={(e) => handleBasePriceChange(e.target.value)}
                    required
                    data-testid="product-price-input"
                    placeholder="Enter monthly base price"
                  />
                  <p className="text-xs text-slate-500">This will auto-calculate all billing cycle prices</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>Billing Cycle Prices (INR)</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Uncheck to disable a billing cycle for this product</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {BILLING_CYCLES.map((cycle) => {
                      const isEnabled = formData.enabled_billing_cycles.includes(cycle.key);
                      return (
                        <div key={cycle.key} className={`space-y-1.5 p-2 rounded-lg border transition-colors ${isEnabled ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50'}`}>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`enable-${cycle.key}`}
                              checked={isEnabled}
                              onChange={() => handleToggleBillingCycle(cycle.key)}
                              className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                              data-testid={`enable-cycle-${cycle.key}`}
                            />
                            <Label htmlFor={`enable-${cycle.key}`} className={`text-xs cursor-pointer ${isEnabled ? 'text-slate-700' : 'text-slate-400'}`}>
                              {cycle.label}
                            </Label>
                          </div>
                          <Input
                            id={cycle.key}
                            type="number"
                            step="0.01"
                            value={formData.billing_prices[cycle.key] || ''}
                            onChange={(e) => handleBillingPriceChange(cycle.key, e.target.value)}
                            placeholder={`${cycle.months} month(s)`}
                            disabled={!isEnabled}
                            data-testid={`billing-price-${cycle.key}`}
                            className={!isEnabled ? 'opacity-40 cursor-not-allowed bg-slate-100' : ''}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="features">Features (comma-separated)</Label>
                  <Input
                    id="features"
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    placeholder="Feature 1, Feature 2, Feature 3"
                    data-testid="product-features-input"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="product-submit-button">
                    {editProduct ? 'Update' : 'Create'} Product
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-colors relative
                  ${activeTab === tab.id 
                    ? 'text-blue-600' 
                    : 'text-slate-500 hover:text-slate-700'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Products Tab Content */}
        {activeTab === 'products' && (
          <>
            {/* Filters */}
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-40 bg-white" data-testid="category-filter">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {allCategoryNames.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search ..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-32 md:w-64 bg-white"
                      data-testid="search-products"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card className="bg-white border-0 shadow-sm" data-testid="products-table-card">
              <CardContent className="p-0 w-full max-w-full overflow-hidden rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-slate-500">No products found</p>
              </div>
            ) : (
         <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 w-12"></th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Product Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProducts.map((product) => {
                const billingPrices = getBillingPrices(product);
                const isExpanded = expandedProduct === product.id;

                return (
                  <Fragment key={product.id}>
                    {/* Main Product Row */}
                    <tr data-testid={`product-row-${product.id}`}>
                      <td className="px-4 py-3 align-middle">
                        <button
                          onClick={() => toggleExpand(product.id)}
                          className="p-1 hover:bg-blue-700 rounded bg-blue-600"
                          data-testid={`expand-product-${product.id}`}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-white" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-white" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="font-medium text-slate-800">{product.name}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 align-middle text-nowrap">{product.category}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800 align-middle">
                        ₹{billingPrices.monthly?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <Badge
                          className={
                            product.is_active !== false
                              ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-50"
                              : "bg-red-50 text-red-700 border-red-200 hover:bg-red-50"
                          }
                        >
                          {product.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          {canEditProduct && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(product)}
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-600 border"
                              data-testid={`edit-category-${product.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteProduct && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(product.id)}
                              title="Delete"
                              className="h-8 w-8 text-red-600 hover:text-red-700 border"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Full-Width Card (Covering All Columns Perfectly) */}
                    {isExpanded && (
                      <tr className="bg-slate-50/50">
                        <td colSpan={6} className="p-4 w-full">
                          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 w-full shadow-sm">
                            <h4 className="font-semibold text-sm mb-3 text-slate-700">
                              All Billing Cycle Prices
                            </h4>
                            
                            {/* Responsive Grid that fills entire table width */}
                            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3 w-full">
                              {BILLING_CYCLES.map((cycle) => {
                                const isEnabled = !product.enabled_billing_cycles || product.enabled_billing_cycles.includes(cycle.key);
                                return (
                                  <div
                                    key={cycle.key}
                                    className={`rounded-lg p-3 border shadow-sm ${isEnabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-dashed border-slate-200 opacity-50'}`}
                                  >
                                    <div className="flex items-center gap-1 mb-1">
                                      {!isEnabled && <span className="text-xs text-red-400 font-medium">Off</span>}
                                      <p className="text-xs text-slate-500 font-medium">{cycle.label}</p>
                                    </div>
                                    <p className={`text-sm font-bold mt-0.5 ${isEnabled ? 'text-blue-600' : 'text-slate-400 line-through'}`}>
                                      ₹{billingPrices[cycle.key]?.toLocaleString() || 'N/A'}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Features */}
                            {product.features && product.features.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-slate-200/60">
                                <h4 className="font-semibold text-sm mb-2 text-slate-700">Features</h4>
                                <div className="flex flex-wrap gap-2">
                                  {product.features.map((feature, idx) => (
                                    <span
                                      key={idx}
                                      className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md border border-blue-100"
                                    >
                                      {feature}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Categories Tab Content */}
        {activeTab === 'categories' && (
          <>
            {/* Add Category Button */}
            <div className="flex justify-end my-2">
              {canCreateCategory && (
                <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                  setCategoryDialogOpen(open);
                  if (!open) resetCategoryForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </Button>
                  </DialogTrigger>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
                    <DialogDescription>
                      {editCategory ? 'Update the category details below.' : 'Create a new product category.'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cat-name">Category Name *</Label>
                      <Input
                        id="cat-name"
                        value={categoryFormData.name}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                        required
                        placeholder="e.g., Shared Hosting"
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cat-desc">Description</Label>
                      <Textarea
                        id="cat-desc"
                        value={categoryFormData.description}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                        placeholder="Brief description of this category..."
                        rows={2}
                        data-testid="category-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <Select 
                          value={categoryFormData.icon} 
                          onValueChange={(value) => setCategoryFormData({ ...categoryFormData, icon: value })}
                        >
                          <SelectTrigger data-testid="category-icon-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(CATEGORY_ICONS).map((iconName) => {
                              const Icon = CATEGORY_ICONS[iconName];
                              return (
                                <SelectItem key={iconName} value={iconName}>
                                  <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4" />
                                    <span>{iconName}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <Select 
                          value={categoryFormData.color} 
                          onValueChange={(value) => setCategoryFormData({ ...categoryFormData, color: value })}
                        >
                          <SelectTrigger data-testid="category-color-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_COLORS.map((color) => (
                              <SelectItem key={color.value} value={color.value}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-4 h-4 rounded ${color.value.split(' ')[0]}`}></span>
                                  <span>{color.label}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cat-order">Sort Order</Label>
                      <Input
                        id="cat-order"
                        type="number"
                        value={categoryFormData.sort_order}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, sort_order: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="category-sort-input"
                      />
                      <p className="text-xs text-slate-500">Lower numbers appear first</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-category-button">
                        {editCategory ? 'Update' : 'Create'} Category
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </div>

            {/* Categories Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-5">
              {categories.map((category) => {
                const CategoryIcon = getCategoryIcon(category.icon);
                return (
                  <Card key={category.id} className="bg-white border shadow-sm hover:shadow-md transition-shadow" data-testid={`category-card-${category.id}`}>
                    <CardContent className="p-4 h-full flex justify-between flex-wrap">
                      <div className="flex items-start justify-between w-full">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${category.color || 'bg-blue-100 text-blue-700'}`}>
                            <CategoryIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{category.name}</h3>
                            <p className="text-sm text-slate-500">{category.description || 'No description'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {canEditCategory && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCategory(category)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600 border"
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteCategory && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteCategoryDialog(category)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 border"
                              data-testid={`delete-category-${category.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 text-sm flex justify-between flex-wrap w-full items-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewCategoryProducts(category)}
                          className="flex items-center gap-2 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 border-cyan-200"
                          data-testid={`view-category-products-${category.id}`}
                        >
                          <FolderOpen className="h-4 w-4" />
                          <span>{getCategoryProducts(category.name).length} {getCategoryProducts(category.name).length === 1 ? 'Product' : 'Products'}</span>
                        </Button>
                        <Badge variant="outline" className="text-xs">
                          Order: {category.sort_order || 0}
                        </Badge>
                      </div>
                      {!category.is_active && (
                        <Badge className="mt-2 bg-red-50 text-red-700 border-red-200">Inactive</Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {categories.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>No categories found. Create your first category to get started.</p>
                </div>
              )}
            </div>

            {/* Delete Category Confirmation Dialog */}
            <Dialog open={!!deleteCategoryDialog} onOpenChange={(open) => !open && setDeleteCategoryDialog(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" />
                    Delete Category
                  </DialogTitle>
                  <DialogDescription className="text-left">
                    Are you sure you want to delete "{deleteCategoryDialog?.name}"?
                  </DialogDescription>
                </DialogHeader>
                {deleteCategoryDialog?.product_count > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Warning:</strong> This category has {deleteCategoryDialog.product_count} product(s). 
                    You must move or delete these products before deleting this category.
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setDeleteCategoryDialog(null)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteCategory}
                    disabled={deleteCategoryDialog?.product_count > 0}
                    data-testid="confirm-delete-category"
                  >
                    Delete Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Category Products Dialog */}
            <Dialog open={!!categoryProductsDialog} onOpenChange={(open) => { if (!open) { setCategoryProductsDialog(null); setEditingProductInCategory(null); } }}>
              <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-cyan-600" />
                    Products in "{categoryProductsDialog?.name}"
                  </DialogTitle>
                </DialogHeader>
                
                {categoryProductsDialog && (
                  <div className="space-y-4">
                    {getCategoryProducts(categoryProductsDialog.name).length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />

                          <p className="mb-3">
                            No products in this category yet.
                          </p>
                        </div>
                      ) : (
                      <div className="space-y-3">
                        {getCategoryProducts(categoryProductsDialog.name).map((product) => (
                          <div key={product.id} className="border rounded-lg p-4 bg-slate-50 mt-2">
                            {editingProductInCategory?.id === product.id ? (
                              // Edit Form
                              <form onSubmit={handleSaveProductInCategory} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label>Product Name *</Label>
                                    <Input
                                      value={categoryProductFormData.name}
                                      onChange={(e) => handleCategoryProductFormChange('name', e.target.value)}
                                      required
                                      data-testid={`edit-product-name-${product.id}`}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Base Price (INR) *</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={categoryProductFormData.base_price}
                                      onChange={(e) => handleCategoryProductFormChange('base_price', e.target.value)}
                                      required
                                      data-testid={`edit-product-price-${product.id}`}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Description</Label>
                                  <Input
                                    value={categoryProductFormData.description}
                                    onChange={(e) => handleCategoryProductFormChange('description', e.target.value)}
                                    data-testid={`edit-product-desc-${product.id}`}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Billing Prices (INR)</Label>
                                  <div className="grid grid-cols-3 gap-2">
                                    {BILLING_CYCLES.map((cycle) => {
                                      const isEnabled = categoryProductFormData.enabled_billing_cycles.includes(cycle.key);
                                      return (
                                        <div key={cycle.key} className={`p-1.5 rounded border transition-colors ${isEnabled ? 'border-slate-200' : 'border-dashed border-slate-200 bg-slate-50'}`}>
                                          <div className="flex items-center gap-1.5 mb-1">
                                            <input
                                              type="checkbox"
                                              checked={isEnabled}
                                              onChange={() => handleToggleBillingCycleInCategory(cycle.key)}
                                              className="w-3 h-3 accent-blue-600 cursor-pointer"
                                            />
                                            <Label className={`text-xs cursor-pointer ${isEnabled ? 'text-slate-700' : 'text-slate-400'}`}>{cycle.label}</Label>
                                          </div>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={categoryProductFormData.billing_prices[cycle.key] || ''}
                                            onChange={(e) => handleCategoryProductBillingPriceChange(cycle.key, e.target.value)}
                                            placeholder={`${cycle.months}mo`}
                                            disabled={!isEnabled}
                                            className={!isEnabled ? 'opacity-40 cursor-not-allowed h-7 text-xs' : 'h-7 text-xs'}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Features (comma-separated)</Label>
                                  <Input
                                    value={categoryProductFormData.features}
                                    onChange={(e) => handleCategoryProductFormChange('features', e.target.value)}
                                    placeholder="Feature 1, Feature 2"
                                    data-testid={`edit-product-features-${product.id}`}
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button type="button" variant="outline" onClick={cancelEditProductInCategory}>
                                    Cancel
                                  </Button>
                                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid={`save-product-${product.id}`}>
                                    Save Changes
                                  </Button>
                                </div>
                              </form>
                            ) : (
                              // View Mode
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4 text-cyan-600" />
                                    <h4 className="font-medium text-slate-800">{product.name}</h4>
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">{product.description || 'No description'}</p>
                                  <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
                                    <span className="text-green-600 font-medium">₹{product.base_price}/mo</span>
                                    {product.features && product.features.length > 0 && (
                                      <span className="text-slate-400">
                                        {product.features.slice(0, 2).join(', ')}{product.features.length > 2 ? '...' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {canEditProduct && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditProductInCategory(product)}
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                    data-testid={`edit-product-in-category-${product.id}`}
                                  >
                                    <Edit2 className="h-4 w-4 mr-1" />
                                    Edit
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex justify-end pt-4">
                  {canCreateProduct && (
                        <Button
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              category: categoryProductsDialog.name,
                            }));

                            setEditProduct(null);
                            setDialogOpen(true);
                          }}
                          className="bg-blue-500 hover:bg-blue-600 mr-3"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Product
                        </Button>
                      )}
                  <Button variant="outline" onClick={() => { setCategoryProductsDialog(null); setEditingProductInCategory(null); }}>
                    Close
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Domain Registration Tab */}
        {activeTab === 'domains' && (
          <DomainRegistrationAdmin />
        )}

        {/* Catalog Products Tab */}
        {activeTab === 'catalog' && (
          <CatalogProducts />
        )}

      </div>
    </DashboardLayout>
  );
}
